import { atom } from 'jotai';
import type { MineralType } from 'types/minerals';
import type {
  DcaForecastParams,
  TypeCurveVolumeRecord,
  TypeCurveRatioRecord,
} from 'types/api-responses';

// Latest DCA fit hyper-params per stream, in the persisted snake_case
// `dca_params` shape. Written on Fit success in `useTcDcaRegression` and read by
// the Type Curve Volumes verification tasks so the stream's params can be
// attached (as `dca_forecasts`) to the task-update request. Lives outside the
// DCA modal's provider, which unmounts (and discards its form/fit state) on
// close.
export const tcDcaFitParamsAtom = atom<
  Partial<Record<MineralType, DcaForecastParams>>
>({});

// Segmentation + outlier month-online indices from the same fit, per stream.
// The regression returns these as numeric strings (`segmentStartDates`,
// `outlierDates`); stored here as number[] so the Type Curve Volumes
// verification task can attach them (alongside `dca_params`) on save. Kept
// separate from `tcDcaFitParamsAtom` so that atom's shape/consumers are untouched.
export interface StreamFitSegments {
  segmentMonthsOnline: number[];
  outlierMonthsOnline: number[];
}

export const tcDcaFitSegmentsAtom = atom<
  Partial<Record<MineralType, StreamFitSegments>>
>({});

/** Aggregation type used for the last fit per stream, in snake_case API format
 *  (e.g. 'p50', 'arithmetic_mean'). Read by the Volumes verification task to
 *  include in the dcaForecasts payload alongside dca_params. */
export const tcDcaFitAggregationAtom = atom<
  Partial<Record<MineralType, string>>
>({});

/** DCA scenario ID used for the last fit per stream. Read by the Volumes
 *  verification task to include in the dcaForecasts payload — the backend
 *  requires dca_scenario_id (NOT NULL) on type_curve_dca_forecast_parameters. */
export const tcDcaFitScenarioIdAtom = atom<
  Partial<Record<MineralType, number>>
>({});

/** Forecast volumes from the last DCA bulk_forecasts call, committed on Save.
 *  Lives outside the modal so the main-page volume charts can read it. */
export interface TcDcaForecastVolumes {
  volume: TypeCurveVolumeRecord[];
  cumulative: TypeCurveVolumeRecord[];
  ratio: TypeCurveRatioRecord[];
  materialBalanceTime: TypeCurveVolumeRecord[];
}

export const tcDcaForecastVolumesAtom = atom<TcDcaForecastVolumes | null>(null);
